import React, { useContext, useMemo, useState } from 'react'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * CaseChangeTime — Case Manager Portal port of Scale's "Change Time" dialog
 * (scalemediationportal ChangeStartTime.jsx). Moves an already-booked mediation
 * to a different hour on the SAME day, leaving the availability slot alone.
 *
 * Distinct from Reschedule, which moves the case into a different published
 * slot (CaseReschedule.jsx). A party often wants the same day at a different
 * hour, which Reschedule cannot express when the mediator only publishes one
 * start per slot.
 *
 * The write is NOT re-implemented here: it proxies to cm-be
 * `PATCH /cases/:id/start-time` (startTimeController), which scope-checks the
 * acting case manager and forwards to scale-be `/case/change-start-time` —
 * the same endpoint Scale Portal uses, so cases.case_schedule_time, the Zoom
 * meeting and the Google Calendar event all move identically.
 *
 * "Email the parties" defaults to OFF and resets each time the dialog opens.
 * The backend treats a missing/false flag as "do not send", matching Scale.
 *
 * Save is disabled while the chosen time equals the current one: cm-be rejects
 * a no-op same-time write with 400 ("already starts at that time"), so the
 * control is gated rather than surfacing that as an error toast.
 */

const SHOW_MERIDIEM = true

/* One number field plus its up/down stepper. Holds its own draft string so the
 * box can be cleared and retyped without the parent snapping it back mid-edit;
 * the draft is committed on blur or Enter and dropped when the parent value
 * moves (a stepper click, or the sibling field wrapping past an hour). */
const TimePart = ({ value, ariaLabel, onCommit, onUp, onDown }) => {
    const [draft, setDraft] = useState(null)
    const [lastSeen, setLastSeen] = useState(value)
    if (value !== lastSeen) {
        setLastSeen(value)
        setDraft(null)
    }
    const commit = () => {
        if (draft !== null) onCommit(draft)
        setDraft(null)
    }
    const Chevron = ({ up, onClick }) => (
        <button
            type="button"
            tabIndex={-1}
            onClick={onClick}
            aria-label={`${up ? 'increase' : 'decrease'} ${ariaLabel}`}
            className="flex h-4 w-4 items-center justify-center text-gray-400 hover:text-gray-700"
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3 w-3"
            >
                <path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
            </svg>
        </button>
    )
    return (
        <div className="flex items-center gap-0.5">
            <input
                type="text"
                inputMode="numeric"
                aria-label={ariaLabel}
                value={draft ?? value}
                onChange={(e) =>
                    setDraft(e.target.value.replace(/\D/g, '').slice(0, 2))
                }
                onFocus={(e) => e.target.select()}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault()
                        commit()
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        onUp()
                    }
                    if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        onDown()
                    }
                }}
                className="w-9 bg-transparent text-center text-xl font-bold text-gray-900 outline-none"
            />
            <div className="flex flex-col">
                <Chevron up onClick={onUp} />
                <Chevron onClick={onDown} />
            </div>
        </div>
    )
}

export default function CaseChangeTime({ onToast, onChanged }) {
    const { caseData, is_cancled_case } = useContext(CaseDetailContext)
    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [notifyParties, setNotifyParties] = useState(false)
    const [newTime, setNewTime] = useState('')

    // "09:00:00" -> "09:00"
    const currentTime = useMemo(() => {
        const raw = caseData?.case_schedule_time || caseData?.scheduleTime
        if (!raw || typeof raw !== 'string') return ''
        const [h, m] = raw.split(':')
        return h && m ? `${h}:${m}` : ''
    }, [caseData?.case_schedule_time, caseData?.scheduleTime])

    /* Minutes-since-midnight at the core; convert to "HH:mm" only at the edges
     * so stepping past 11 PM or below 12:00 AM costs one modulo, not a branch. */
    const toMins = (hhmm) => {
        if (!hhmm) return null
        const [h, m] = hhmm.split(':').map(Number)
        if (Number.isNaN(h) || Number.isNaN(m)) return null
        return h * 60 + m
    }
    const toHHmm = (mins) => {
        const wrapped = ((mins % 1440) + 1440) % 1440
        return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(
            wrapped % 60
        ).padStart(2, '0')}`
    }
    // "09:30" -> "9:30 AM"
    const label = (hhmm) => {
        if (!hhmm) return ''
        const [h, m] = hhmm.split(':').map(Number)
        const period = h >= 12 ? 'PM' : 'AM'
        const hour12 = h % 12 === 0 ? 12 : h % 12
        return `${hour12}:${String(m).padStart(2, '0')} ${period}`
    }

    const selectedTime = newTime || currentTime
    const selectedMins = toMins(selectedTime)
    const unchanged = !selectedTime || selectedTime === currentTime

    const parts = useMemo(() => {
        if (selectedMins === null) return { hh: '', mm: '', pm: false }
        const h24 = Math.floor(selectedMins / 60)
        const hour12 = h24 % 12 === 0 ? 12 : h24 % 12
        const shown = SHOW_MERIDIEM ? hour12 : h24
        return {
            hh: String(shown).padStart(2, '0'),
            mm: String(selectedMins % 60).padStart(2, '0'),
            pm: h24 >= 12,
        }
    }, [selectedMins])

    const bump = (deltaMins) => {
        if (selectedMins === null) return
        setNewTime(toHHmm(selectedMins + deltaMins))
    }
    const setMeridiem = (wantPm) => {
        if (selectedMins === null || parts.pm === wantPm) return
        setNewTime(toHHmm(selectedMins + (wantPm ? 720 : -720)))
    }
    const commitHour = (raw) => {
        const n = Number(raw)
        if (raw === '' || Number.isNaN(n)) return
        let h24
        if (SHOW_MERIDIEM) {
            if (n < 1 || n > 12) return
            h24 = (n % 12) + (parts.pm ? 12 : 0)
        } else {
            if (n < 0 || n > 23) return
            h24 = n
        }
        setNewTime(toHHmm(h24 * 60 + (selectedMins % 60)))
    }
    const commitMinute = (raw) => {
        const n = Number(raw)
        if (raw === '' || Number.isNaN(n) || n < 0 || n > 59) return
        setNewTime(toHHmm(Math.floor(selectedMins / 60) * 60 + n))
    }

    const openDialog = () => {
        setNewTime(currentTime)
        setNotifyParties(false)
        setDialogIsOpen(true)
    }

    const handleSave = async () => {
        if (!caseData?.id || unchanged) return
        try {
            setIsSaving(true)
            await api.patch(`/cases/${caseData.id}/start-time`, {
                case_schedule_time: `${selectedTime}:00`,
                notify_parties: notifyParties,
            })
            setDialogIsOpen(false)
            onToast?.({
                type: 'success',
                message: notifyParties
                    ? 'Start time updated and parties notified'
                    : 'Start time updated',
            })
            if (onChanged) onChanged()
        } catch (err) {
            console.error('changeStartTime failed :: ', err)
            onToast?.({
                type: 'error',
                message:
                    err?.response?.data?.message ||
                    'Could not update the start time. Please try again.',
            })
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="w-full m-0 p-0 h-full flex items-stretch">
            <button
                type="button"
                onClick={() => !is_cancled_case && openDialog()}
                disabled={is_cancled_case}
                title={is_cancled_case ? 'Case is already canceled!' : ''}
                className={`bg-white border border-gray-300 text-gray-600 rounded-md h-11 px-4 py-2 flex justify-between items-center w-full transition-colors hover:bg-gray-50 ${
                    is_cancled_case ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
                <span className="text-sm font-medium leading-tight">
                    Change Time
                </span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-5 w-5 flex-shrink-0 text-gray-500"
                >
                    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm1-13h-2v6l5 3 1-1.73-4-2.37z" />
                </svg>
            </button>

            {dialogIsOpen && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
                    onClick={() => !isSaving && setDialogIsOpen(false)}
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-b pb-2">
                            <h5 className="text-lg font-semibold mb-1">
                                Change Start Time
                            </h5>
                        </div>
                        <div className="mt-5">
                            <div className="mb-3 flex items-baseline justify-between gap-3">
                                <span
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#374151',
                                    }}
                                >
                                    Start time
                                </span>
                                {currentTime && (
                                    <span className="text-xs text-gray-500">
                                        Currently starts at{' '}
                                        <span className="font-semibold text-gray-700">
                                            {label(currentTime)}
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className="flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2">
                                <TimePart
                                    value={parts.hh}
                                    ariaLabel="hours"
                                    onCommit={commitHour}
                                    onUp={() => bump(60)}
                                    onDown={() => bump(-60)}
                                />
                                <span className="text-xl font-bold text-gray-400 select-none">
                                    :
                                </span>
                                <TimePart
                                    value={parts.mm}
                                    ariaLabel="minutes"
                                    onCommit={commitMinute}
                                    onUp={() => bump(5)}
                                    onDown={() => bump(-5)}
                                />
                                {SHOW_MERIDIEM && (
                                    <div className="ml-auto flex items-center rounded-lg bg-gray-100 p-1">
                                        {[false, true].map((isPm) => (
                                            <button
                                                key={isPm ? 'PM' : 'AM'}
                                                type="button"
                                                onClick={() => setMeridiem(isPm)}
                                                className={`rounded-md px-2.5 py-1 text-xs font-bold transition-colors ${
                                                    parts.pm === isPm
                                                        ? 'bg-white text-blue-600 shadow-sm'
                                                        : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                {isPm ? 'PM' : 'AM'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <label className="mt-5 flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={notifyParties}
                                    onChange={(e) =>
                                        setNotifyParties(e.target.checked)
                                    }
                                />
                                <span className="text-sm text-gray-700">
                                    Email the parties about the case time change
                                </span>
                            </label>
                        </div>
                        <div className="w-full flex justify-between gap-3 mt-6">
                            <button
                                type="button"
                                disabled={isSaving}
                                className="w-full bg-[#EBEBEB] text-black font-semibold rounded-xl py-3"
                                onClick={() => setDialogIsOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={isSaving || unchanged}
                                onClick={handleSave}
                                className={`w-full rounded-xl py-3 text-white font-semibold ${
                                    isSaving || unchanged
                                        ? 'bg-[#173336]/50 cursor-not-allowed'
                                        : 'bg-[#173336] hover:bg-[#173336]'
                                }`}
                            >
                                {isSaving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
